/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useRef } from 'react';
import type { PortalPublication } from './types';

/** How often the rows are refreshed while something is still being created. */
export const PENDING_REFRESH_INTERVAL_MS = 4000;

/** Gives up after this many refreshes (~6 minutes), so a stuck job cannot poll forever. */
export const PENDING_REFRESH_MAX_TICKS = 90;

/**
 * A job still "queued" this long after it was enqueued is stuck, not in
 * flight. The worker queue gives a job up to 3 attempts of 180 s visibility
 * (~9 min) before dead-lettering it -- and a dead-lettered job never writes a
 * final status, so its row stays "queued" forever. Past this age, polling can
 * only repeat the same answer.
 */
export const QUEUED_STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * Whether the entry is waiting on the worker: queued, or its Lead Ad (only
 * ever "pending" on a queued entry) has not been attempted yet.
 */
function isAwaitingWorker(pub: PortalPublication): boolean {
  return pub.status === 'queued' || pub.lead_ad_status === 'pending';
}

/**
 * When the entry entered the queue, in epoch ms. Prefers `queued_at` (always
 * the latest enqueue); falls back to `published_at` for entries written before
 * `queued_at` existed. Null when neither is present or parseable.
 */
function enqueuedAtMs(pub: PortalPublication): number | null {
  const raw = pub.queued_at ?? pub.published_at;
  const ms = raw ? Date.parse(raw) : Number.NaN;
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Whether the entry has waited longer than {@link QUEUED_STALE_AFTER_MS}. An
 * entry with no usable timestamp is not called stale -- we cannot tell, and
 * the tick cap still bounds the polling.
 */
function isStale(pub: PortalPublication, now: number): boolean {
  const since = enqueuedAtMs(pub);
  return since !== null && now - since >= QUEUED_STALE_AFTER_MS;
}

/**
 * Whether any publish is still being worked on: queued for the worker, or
 * its Lead Ad is still being created. Both finish asynchronously, after the
 * request that started them has returned. A job queued longer than
 * {@link QUEUED_STALE_AFTER_MS} ago is stuck and no longer counts.
 *
 * @param publications - The live per-portal publish status.
 * @param now - Current time in epoch ms; injectable so it is testable.
 * @returns True while something is in flight.
 */
export function hasInFlightWork(
  publications: Record<string, PortalPublication>,
  now: number = Date.now(),
): boolean {
  return Object.values(publications).some(
    (pub) => isAwaitingWorker(pub) && !isStale(pub, now),
  );
}

/**
 * Keeps the publication rows fresh while a publish or Lead Ad is in flight,
 * so the row moves from "creating" to "created"/"failed" without a manual
 * reload. Stops as soon as nothing is pending.
 *
 * @param publications - The live per-portal publish status.
 * @param refresh - Re-fetches the rows' data.
 */
export function usePendingPublicationsRefresh(
  publications: Record<string, PortalPublication>,
  refresh: () => Promise<void>,
): void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const inFlight = hasInFlightWork(publications);

  useEffect(() => {
    if (!inFlight) return undefined;
    let ticks = 0;
    const timer = setInterval(() => {
      ticks += 1;
      if (ticks > PENDING_REFRESH_MAX_TICKS) {
        clearInterval(timer);
        return;
      }
      void refreshRef.current();
    }, PENDING_REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [inFlight]);
}
